/**
 * Today and tomorrow, side by side, above the task list.
 *
 * Tomorrow earns its place next to today because the useful question at 4pm is
 * not "what is left" but "what am I walking into". Today sits on the left,
 * where reading starts.
 *
 * Deliberately a separate request from the dashboard summary. Refreshing a
 * published calendar means fetching several hundred kilobytes from Microsoft,
 * which measured 5.5 seconds -- so the task list never waits on it. If the
 * calendar is slow or down, these tiles arrive late or not at all and the rest
 * of the page does not notice.
 *
 * Nothing renders at all when no calendar is connected. Somebody who never
 * sets one up should not have two permanent empty boxes on their dashboard.
 */

import { useEffect, useMemo, useState } from 'react'

import { api } from '@/api/client'
import type { CalendarDay, CalendarEvent } from '@/api/types'
import { EventReminder } from '@/components/EventReminder'
import { useEventReminders } from '@/hooks/useEventReminders'
import { unlockChime } from '@/lib/chime'
import { readableTextOn } from '@/lib/color'
import { parseTimestamp } from '@/lib/format'

/** Wall-clock time where the user is.
 *
 * parseTimestamp, not `new Date(iso)`: the API sends naive UTC with no zone
 * suffix, and JavaScript reads that as *local* time. Getting this wrong put
 * every meeting six hours late, which looks plausible enough on a dashboard to
 * survive a glance. */
function clock(iso: string): string {
  return parseTimestamp(iso).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })
}

function when(event: CalendarEvent): string {
  if (event.is_all_day) return 'All day'
  const start = clock(event.starts_at)
  return event.ends_at ? `${start} – ${clock(event.ends_at)}` : start
}

function isNow(event: CalendarEvent, now: number): boolean {
  if (event.is_all_day) return false
  const start = parseTimestamp(event.starts_at).getTime()
  const end = event.ends_at ? parseTimestamp(event.ends_at).getTime() : start
  return now >= start && now < end
}

function isPast(event: CalendarEvent, now: number): boolean {
  if (event.is_all_day) return false
  return now >= parseTimestamp(event.ends_at ?? event.starts_at).getTime()
}

/** Local YYYY-MM-DD, offset by whole days. The server asks for a local date,
 *  so this must not go through UTC on the way. */
function isoDate(offsetDays: number): string {
  const date = new Date()
  date.setDate(date.getDate() + offsetDays)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function DayTile({
  title,
  busy,
  day,
  now,
  /** Only today greys out what has already happened; on tomorrow nothing has. */
  markProgress,
}: {
  title: string
  /** Transient, and the only thing beside the title: a count there restated
   *  what the rows underneath already show. This does not -- it explains why
   *  the rows might be a moment out of date. */
  busy?: boolean
  day: CalendarDay
  now: number
  markProgress: boolean
}) {
  return (
    <section aria-labelledby={`cal-${title}`} className="card overflow-hidden">
      <header className="flex flex-wrap items-baseline gap-2 border-b border-line px-3 py-2">
        <h2 id={`cal-${title}`} className="text-sm font-semibold">
          {title}
        </h2>
        {busy && <span className="text-xs text-ink-3">checking for changes…</span>}
        {day.stale && (
          <span
            className="pill bg-warning-light text-warning-text"
            title="The calendar could not be refreshed recently, so this may be out of date."
          >
            may be out of date
          </span>
        )}
      </header>

      {day.events.length === 0 ? (
        <p className="px-3 py-6 text-center text-sm text-ink-3">Nothing scheduled.</p>
      ) : (
        <ul className="m-0 list-none p-0">
          {day.events.map((event) => {
            const colour = day.feed_colors[event.feed_id] ?? '#004077'
            const current = markProgress && isNow(event, now)
            const past = markProgress && isPast(event, now)

            return (
              <li
                key={event.id}
                className={`flex items-baseline gap-2 border-b border-line px-3 py-2 last:border-b-0 ${
                  current ? 'bg-navy-softer' : ''
                }`}
              >
                {/* Which calendar it came from, in one character of space. */}
                <span
                  aria-hidden
                  className="size-2 shrink-0 rounded-full"
                  style={{ backgroundColor: colour }}
                  title={day.feed_names[event.feed_id]}
                />
                <span
                  className={`shrink-0 text-xs font-semibold tabular-nums ${
                    past ? 'text-ink-3' : 'text-ink-2'
                  }`}
                >
                  {when(event)}
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className={`block text-sm ${
                      past ? 'text-ink-3 line-through decoration-1' : 'font-medium text-ink'
                    }`}
                  >
                    {event.summary || 'Untitled'}
                  </span>
                  {event.location && (
                    <span className="block truncate text-xs text-ink-3" title={event.location}>
                      {event.location}
                    </span>
                  )}
                </span>
                {current && (
                  <span
                    className="pill shrink-0"
                    style={{ backgroundColor: colour, color: readableTextOn(colour) }}
                  >
                    now
                  </span>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

/** How often an open dashboard re-reads the days. The server refreshes the
 *  feeds themselves at most every 30 minutes, so asking more often than this
 *  would mostly re-read the same cache; asking less often leaves a tab open
 *  since breakfast showing breakfast's calendar. */
const POLL_MS = 10 * 60 * 1000

export function CalendarStrip() {
  const [today, setToday] = useState<CalendarDay | null>(null)
  const [tomorrow, setTomorrow] = useState<CalendarDay | null>(null)
  const [failed, setFailed] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  // Re-rendered on a timer so "now" moves through the day without a reload.
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    let stopped = false
    let inFlight = false
    // Effect-scoped rather than read from state: `today` here would be the
    // value captured at mount, which is null forever, so a single failed poll
    // would have hidden a strip that was showing perfectly good meetings.
    let everLoaded = false

    async function load() {
      // A slow fetch must not stack up behind a tab that keeps waking.
      if (inFlight || stopped) return
      inFlight = true
      try {
        // Dates are recomputed on every load rather than captured once, so a
        // dashboard left open overnight rolls over to the new day by itself
        // instead of labelling yesterday "Today".
        // Both at once: the second is served from the same cache the first
        // refreshed, so it costs a query rather than another fetch upstream.
        const [first, second] = await Promise.all([
          api.calendars.day(isoDate(0)),
          api.calendars.day(isoDate(1)),
        ])
        if (stopped) return
        everLoaded = true
        setToday(first)
        setTomorrow(second)
        setFailed(false)
      } catch {
        // A calendar problem is not a dashboard problem: on the first load say
        // nothing and let the rest of the page get on with it. On a later poll
        // keep whatever is already on screen -- stale meetings beat none.
        if (!stopped && !everLoaded) setFailed(true)
      } finally {
        inFlight = false
      }
    }

    // Cache first, so the tiles are on screen in milliseconds...
    void load()

    // ...then pull both calendars fresh and show what comes back. Every page
    // load fetches upstream, which is the point: a meeting added in Outlook
    // ten minutes ago should be here after a reload, not after the next
    // half-hourly window. It costs several seconds, spent behind an already-
    // rendered strip where nobody is waiting on it.
    void (async () => {
      setRefreshing(true)
      try {
        await api.calendars.refreshAll()
        if (!stopped) await load()
      } catch {
        // The cached render stands. A calendar server having a bad day is not
        // a reason to take meetings off the screen.
      } finally {
        if (!stopped) setRefreshing(false)
      }
    })()

    // Polling and tab-focus deliberately do NOT force: they go through the
    // server's own 30-minute window. Forcing here would mean every glance at
    // the tab pulled 1.5MB from Google and Microsoft.
    const timer = setInterval(() => void load(), POLL_MS)

    // Coming back to the tab is the moment staleness is most obvious and most
    // likely -- a laptop reopened after lunch has missed several polls, since
    // browsers throttle timers in background tabs.
    function onVisible() {
      if (document.visibilityState === 'visible') void load()
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      stopped = true
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
    // Deliberately once: load() reads nothing from render scope that changes,
    // and re-running this would restart the timer on every tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fifteen seconds rather than sixty: the clock also decides when a reminder
  // fires, and a minute of slack would make "at start time" arrive up to a
  // minute late. Re-rendering two small lists this often costs nothing.
  useEffect(() => {
    unlockChime()
    const timer = setInterval(() => setNow(Date.now()), 15_000)
    return () => clearInterval(timer)
  }, [])

  if (failed || !today || !tomorrow || !today.has_feeds) return null

  // items-start so a busy day does not stretch a quiet one to match it.
  return (
    <>
      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
        <DayTile title="Today" busy={refreshing} day={today} now={now} markProgress />
        <DayTile title="Tomorrow" day={tomorrow} now={now} markProgress={false} />
      </div>
      <Reminders today={today} tomorrow={tomorrow} now={now} />
    </>
  )
}

/**
 * Tomorrow's events are included deliberately: at 11:55pm, something at
 * 12:05am is five minutes away and belongs in a reminder, even though it is
 * on the other tile.
 */
function Reminders({
  today,
  tomorrow,
  now,
}: {
  today: CalendarDay
  tomorrow: CalendarDay
  now: number
}) {
  const events = useMemo(
    () => [...today.events, ...tomorrow.events],
    [today.events, tomorrow.events],
  )
  const { due, dismiss, snooze } = useEventReminders(events, now)
  const next = due[0]
  if (!next) return null

  const colours = { ...today.feed_colors, ...tomorrow.feed_colors }
  const names = { ...today.feed_names, ...tomorrow.feed_names }

  return (
    <EventReminder
      key={next.id}
      event={next}
      now={now}
      colour={colours[next.feed_id] ?? '#004077'}
      feedName={names[next.feed_id]}
      onDismiss={() => dismiss(next)}
      onSnooze={(leadSeconds) => snooze(next, leadSeconds)}
    />
  )
}
